import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ingredientColor } from "@/components/FlavorGraphCanvas";

const CX = 500;
const CY = 500;
const INNER_R = 205;
const OUTER_R = 388;

function nodeColor(d) {
  if (!d) return "#F97316";
  if (d.ntype === "ChemicalCompound") return "#00E5FF";
  if (d.ntype === "FlavorCategory") return "#EC4899";
  if (d.ntype === "Ingredient") return ingredientColor(d.category);
  return "#F97316";
}

/**
 * Intro procedural e viva: desenha a TEIA REAL do usuário (nós/arestas vindos da
 * base de dados naquele momento), centraliza no núcleo principal e anima o
 * "desabrochar" orgânico das conexões antes de entregar o canvas interativo.
 */
export default function TeiaIntro({ elements = [], centerId, ready = false, onDone }) {
  const [show, setShow] = useState(true);

  const finish = () => {
    setShow(false);
    setTimeout(() => onDone && onDone(), 450);
  };

  const layout = useMemo(() => {
    const nodes = elements.filter((e) => e.data && !e.data.source);
    const edges = elements.filter((e) => e.data && e.data.source);
    if (!nodes.length) return null;

    let center = nodes.find((n) => n.data.id === centerId);
    if (!center) {
      const deg = {};
      edges.forEach((e) => {
        deg[e.data.source] = (deg[e.data.source] || 0) + 1;
        deg[e.data.target] = (deg[e.data.target] || 0) + 1;
      });
      center = nodes.reduce((a, b) => ((deg[b.data.id] || 0) > (deg[a.data.id] || 0) ? b : a), nodes[0]);
    }

    const isInner = (n) => n.data.ntype === "ChemicalCompound" || n.data.ntype === "FlavorCategory";
    const inner = nodes.filter((n) => n !== center && isInner(n)).slice(0, 16);
    const outer = nodes.filter((n) => n !== center && !isInner(n)).slice(0, 28);

    const pos = {};
    pos[center.data.id] = { x: CX, y: CY, ring: 0, d: center.data };
    inner.forEach((n, i) => {
      const a = (i / Math.max(inner.length, 1)) * Math.PI * 2 - Math.PI / 2;
      pos[n.data.id] = { x: CX + Math.cos(a) * INNER_R, y: CY + Math.sin(a) * INNER_R, ring: 1, d: n.data };
    });
    outer.forEach((n, i) => {
      const a = (i / Math.max(outer.length, 1)) * Math.PI * 2 - Math.PI / 2 + 0.18;
      pos[n.data.id] = { x: CX + Math.cos(a) * OUTER_R, y: CY + Math.sin(a) * OUTER_R, ring: 2, d: n.data };
    });

    const drawEdges = edges.filter((e) => pos[e.data.source] && pos[e.data.target]).slice(0, 90);
    return { pos, center, drawEdges };
  }, [elements, centerId]);

  const bloomed = ready && !!layout;

  // encerra após o desabrochar completo
  useEffect(() => {
    if (!bloomed) return;
    const t = setTimeout(finish, 2900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloomed]);

  // salvaguarda: se os dados nunca chegarem, não trava a entrada
  useEffect(() => {
    const t = setTimeout(finish, 7000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const centerLabel = layout?.center?.data?.label || "Sabor";
  const positions = layout ? Object.values(layout.pos).filter((p) => p.ring !== 0) : [];

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="teia-intro"
          className="absolute inset-0 z-50 flex items-center justify-center overflow-hidden bg-[#0B0F17]"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45 }}
        >
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "linear-gradient(#E05A2B 1px, transparent 1px), linear-gradient(90deg, #E05A2B 1px, transparent 1px)",
              backgroundSize: "46px 46px",
            }}
          />

          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid slice">
            {/* fios da teia — arestas reais desabrochando do núcleo para fora */}
            {layout &&
              layout.drawEdges.map((e, i) => {
                const s = layout.pos[e.data.source];
                const t = layout.pos[e.data.target];
                const outerRing = Math.max(s.ring, t.ring);
                const delay = (outerRing === 1 ? 0.3 : 0.85) + (i % 20) * 0.02;
                const color = e.data.etype === "PAIRS_WITH" ? "#E05A2B" : e.data.etype === "HAS_COMPOUND" ? "#1E5A73" : "#2A3650";
                return (
                  <motion.line
                    key={e.data.id}
                    x1={s.x}
                    y1={s.y}
                    x2={t.x}
                    y2={t.y}
                    stroke={color}
                    strokeWidth={e.data.etype === "PAIRS_WITH" ? 1.6 : 1}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.55 }}
                    transition={{ duration: 0.9, delay, ease: "easeOut" }}
                  />
                );
              })}

            {/* nós reais surgindo em anéis orgânicos */}
            {positions.map((p, i) => {
              const delay = (p.ring === 1 ? 0.35 : 0.85) + (i % 24) * 0.03;
              const r = p.ring === 1 ? 8 : 7;
              return (
                <g key={p.d.id}>
                  <motion.circle
                    cx={p.x}
                    cy={p.y}
                    r={r}
                    fill={nodeColor(p.d)}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 16, delay }}
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                  />
                  <motion.text
                    x={p.x}
                    y={p.y - 13}
                    fill="#94A3B8"
                    fontSize="14"
                    textAnchor="middle"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.9 }}
                    transition={{ duration: 0.4, delay: delay + 0.2 }}
                  >
                    {String(p.d.label || "").slice(0, 16)}
                  </motion.text>
                </g>
              );
            })}
          </svg>

          {/* núcleo luminescente — o "Sabor" principal REAL do usuário */}
          <motion.div
            className="relative z-10 flex items-center justify-center"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.05 }}
          >
            <div className="absolute h-44 w-44 rounded-full bg-[#E05A2B] blur-3xl opacity-40 animate-pulse" />
            <div
              className="relative h-32 w-32 rounded-full flex items-center justify-center border border-[#E05A2B]/60 px-3 text-center"
              style={{
                boxShadow: "0 0 60px rgba(224,90,43,0.6), inset 0 0 30px rgba(224,90,43,0.35)",
                background: "radial-gradient(circle,#1b2740,#0B0F17)",
              }}
            >
              <span
                className="text-lg font-bold tracking-tight text-white leading-tight"
                style={{ textShadow: "0 0 18px rgba(224,90,43,0.9)" }}
              >
                {centerLabel}
              </span>
            </div>
          </motion.div>

          {!bloomed && (
            <motion.div
              className="absolute bottom-16 left-1/2 -translate-x-1/2 text-xs text-slate-500"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.4, repeat: Infinity }}
              data-testid="teia-intro-loading"
            >
              tecendo sua teia de sabores…
            </motion.div>
          )}

          <button
            data-testid="teia-intro-skip"
            onClick={finish}
            className="absolute bottom-6 right-6 text-xs text-slate-500 hover:text-[#E05A2B] transition-colors"
          >
            pular ›
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
